import { atom } from "jotai";
import {
  ApprovalFilterInfo,
  ApprovalAction,
  approvalAtom,
  currentPageAtom,
  filterInfoAtom,
  pageLimitAtom,
  approvalActionAtom,
  groupByCheckAtom,
} from "./approval.atoms";
import { AppConfig } from "@/infrastructure/services/config/WebApp.config";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import {
  GetApprovalsUseCase,
  GetApprovalsByRemittanceUseCase,
  ResetApprovalWorkflowUseCase,
  SendForApprovalUseCase,
} from "@/core/usecases/deduction";
import { BrandMember } from "@/core/entities/lookup/brandMember/BrandMember";
import { Deduction } from "@/core/entities/deduction/deduction";

const client = GraphQlAdapter.getInstance().getClient();
const _appConfig = AppConfig.getInstance();
const deductionRepository = new DeductionRepository(client);
const TIME_ZONE = _appConfig.TIMEZONE;

const buildApprovalListParams = (filterInfo: ApprovalFilterInfo, currentPage: number, pageLimit: number) => ({
  page_number: currentPage,
  page_size: pageLimit,
  sort_column: filterInfo.sortColumn,
  sort_order: filterInfo.sortOrder,
  time_zone: TIME_ZONE,
  search: filterInfo.search,
  check_from_date: filterInfo.checkFromDate,
  check_to_date: filterInfo.checkToDate,
  invoice_from_date: filterInfo.invoiceFromDate,
  invoice_to_date: filterInfo.invoiceToDate,
  purchaser_ids: filterInfo.purchaser.map(purchaser => Number(purchaser.value)),
  distribution_center_ids: filterInfo.dc.map(distributionCenter => Number(distributionCenter.value)),
  glCode_ids: filterInfo.glCode.map(glCode => Number(glCode.value)),
  analyst_user_ids: filterInfo.analyst.map(analystUser => Number(analystUser.value)),
  approver_user_ids: filterInfo.approver.map(approverUser => Number(approverUser.value)),
});

// Action atoms
export const getApprovalsAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(approvalAtom, {
        ...get(approvalAtom),
        isLoading: isLoading,
        isRefreshing: isRefreshing,
        error: null,
        filterInfo: {
          ...get(approvalAtom).filterInfo,
          lastFilteredAt: new Date(),
        },
      });
      const filterInfo = get(filterInfoAtom);
      const currentPage = get(currentPageAtom);
      const pageLimit = get(pageLimitAtom);

      const getApprovalsUseCase = new GetApprovalsUseCase(deductionRepository);
      const response = await getApprovalsUseCase.execute(
        buildApprovalListParams(filterInfo, currentPage, pageLimit),
        isLoading || isRefreshing
      );

      set(approvalAtom, {
        ...get(approvalAtom),
        approvals: response?.getApprovals ?? [],
        approvalRemittances: [],
        columns: response?.columns ?? [],
        totalCount: response?.totalCount ?? 0,
        currentPage: response?.currentPage ?? 1,
        pageLimit: response?.pageLimit ?? 0,
        isLoading: false,
        isRefreshing: false,
        error: null,
      });
    } catch (error) {
      console.error(error);
      set(approvalAtom, {
        ...get(approvalAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const getApprovalsByRemittanceAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(approvalAtom, {
        ...get(approvalAtom),
        isLoading: isLoading,
        isRefreshing: isRefreshing,
        error: null,
        filterInfo: {
          ...get(approvalAtom).filterInfo,
          lastFilteredAt: new Date(),
        },
      });
      const filterInfo = get(filterInfoAtom);
      const currentPage = get(currentPageAtom);
      const pageLimit = get(pageLimitAtom);

      const useCase = new GetApprovalsByRemittanceUseCase(deductionRepository);
      const response = await useCase.execute(
        buildApprovalListParams(filterInfo, currentPage, pageLimit),
        isLoading || isRefreshing
      );

      set(approvalAtom, {
        ...get(approvalAtom),
        approvalRemittances: response?.getApprovalsByRemittance ?? [],
        approvals: [],
        columns: response?.columns ?? [],
        totalCount: response?.totalCount ?? 0,
        currentPage: response?.currentPage ?? 1,
        pageLimit: response?.pageLimit ?? 0,
        isLoading: false,
        isRefreshing: false,
        error: null,
      });
    } catch (error) {
      console.error(error);
      set(approvalAtom, {
        ...get(approvalAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const fetchApprovalsListAtom = atom(
  null,
  async (get, set, params: { isLoading?: boolean; isRefreshing?: boolean } = {}) => {
    if (get(groupByCheckAtom)) {
      await set(getApprovalsByRemittanceAtom, params);
    } else {
      await set(getApprovalsAtom, params);
    }
  }
);

export const setGroupByCheckAtom = atom(null, async (get, set, groupByCheck: boolean) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    groupByCheck,
    currentPage: 1,
    selectedApprovals: [],
    selectedApproval: null,
    expandedRemittanceKey: null,
    approvals: [],
    approvalRemittances: [],
  });
  await set(fetchApprovalsListAtom, { isLoading: true, isRefreshing: false });
});

export const setExpandedRemittanceKeyAtom = atom(null, (get, set, remittanceId: number | null) => {
  const current = get(approvalAtom).expandedRemittanceKey;
  set(approvalAtom, {
    ...get(approvalAtom),
    expandedRemittanceKey: current === remittanceId ? null : remittanceId,
  });
});

export const setIsLoadingAtom = atom(null, (get, set, isLoading: boolean) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    isLoading: isLoading,
  });
});
export const setErrorAtom = atom(null, (get, set, error: Error | null) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    error: error,
  });
});
export const setTotalCountAtom = atom(null, (get, set, totalCount: number) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    totalCount: totalCount,
  });
});

export const setCurrentPageAtom = atom(null, (get, set, page: number) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    currentPage: page,
  });
});

export const setFilterAtom = atom(null, (get, set, filter: ApprovalFilterInfo) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    filterInfo: filter,
    userHasClearedApprovalFilters: isDefaultFilter(filter),
  });
});

export const clearFilterAtom = atom(null, (get, set, defaultFilter?: ApprovalFilterInfo) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    isFilterApplied: false,
    userHasClearedApprovalFilters: true,
    filterInfo: defaultFilter ?? {
      lastFilteredAt: null,
      search: null,
      checkFromDate: null,
      checkToDate: null,
      invoiceFromDate: null,
      invoiceToDate: null,
      purchaser: [],
      dc: [],
      glCode: [],
      analyst: [],
      approver: [],
      sortColumn: null,
      sortOrder: null,
      checkDateRange: null,
      invoiceDateRange: null,
    },
  });
});

export const setIsFilterAppliedAtom = atom(null, (get, set, isFilterApplied: boolean) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    isFilterApplied: isFilterApplied,
  });
});

export const isDefaultFilter = (filterInfo: ApprovalFilterInfo) => {
  return Object.entries(filterInfo)
    .filter(([key]) => key !== "lastFilteredAt" && key !== "sortColumn" && key !== "sortOrder")
    .every(([, value]) => {
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return value === null || value === "" || value === undefined;
    });
};

export const setLoggedInBrandMemberAtom = atom(null, (get, set, loggedInBrandMember: BrandMember) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    loggedInBrandMember: loggedInBrandMember,
  });
});

export const setSelectedApprovalsAtom = atom(null, (get, set, selectedApprovals: Deduction[]) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    selectedApprovals: selectedApprovals,
  });
});

export const setIsApprovalModalOpenAtom = atom(null, (get, set, isApprovalModalOpen: boolean) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    isApprovalModalOpen: isApprovalModalOpen,
  });
});

export const setSelectedApprovalAtom = atom(null, (get, set, selectedApproval: Deduction | null) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    selectedApproval: selectedApproval,
  });
});

export const setApprovalActionAtom = atom(null, (get, set, action: ApprovalAction) => {
  set(approvalAtom, {
    ...get(approvalAtom),
    approvalAction: action,
  });
});

export const resetApprovalWorkflowAtom = atom(null, async (get, set, deductionId: number): Promise<boolean> => {
  set(approvalAtom, {
    ...get(approvalAtom),
    isApprovalSending: true,
  });

  try {
    const { resetApprovalWorkflow } = await new ResetApprovalWorkflowUseCase(deductionRepository).execute({
      deductionId: deductionId,
    });

    set(approvalAtom, {
      ...get(approvalAtom),
      isApprovalSending: false,
    });

    return resetApprovalWorkflow;
  } catch (error) {
    console.error(error);
    set(approvalAtom, {
      ...get(approvalAtom),
      isApprovalSending: false,
    });

    return false;
  }
});

export const sendForApprovalBulkAtom = atom(
  null,
  async (get, set, deductions: Deduction[], comment: string, action?: "APPROVE" | "REJECT") => {
    set(approvalAtom, {
      ...get(approvalAtom),
      isApprovalSending: true,
    });
    const currentPage = get(currentPageAtom);
    const approvalAction = action ?? get(approvalActionAtom);
    if (!approvalAction) {
      throw new Error("Approval action is required");
    }
    try {
      const { bulkSendForApproval } = await new SendForApprovalUseCase(deductionRepository).bulk(
        deductions.map(deduction => ({
          deduction_id: deduction.id,
          remittance_item_id: deduction.remittanceItemId,
          comment: comment,
          action: approvalAction,
        })),
        currentPage
      );

      if (bulkSendForApproval) {
        const remainingSelectedApprovals = get(approvalAtom).selectedApprovals.filter(
          eachSelectedApproval =>
            !bulkSendForApproval.some(eachDeduction => eachDeduction.id === eachSelectedApproval.id)
        );

        // Keep approvalAction until the modal closes — clearing it here flips the
        // footer to show both Approve and Return while the modal is still open.
        set(approvalAtom, {
          ...get(approvalAtom),
          selectedApprovals: remainingSelectedApprovals,
          selectedApproval: null,
          isApprovalSending: false,
        });

        await set(fetchApprovalsListAtom, { isLoading: false, isRefreshing: true });
      } else {
        set(approvalAtom, {
          ...get(approvalAtom),
          isApprovalSending: false,
        });
      }

      return bulkSendForApproval;
    } catch (error) {
      console.error(error);
      set(approvalAtom, {
        ...get(approvalAtom),
        isApprovalSending: false,
      });
    }
  }
);
